import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ExternalLink, Filter, Download, Eye, Trash2, RotateCcw, ChevronDown, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Tenders() {
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [dismissedTenders, setDismissedTenders] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalDismissed, setTotalDismissed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    company: 'all',
    priority: 'all',
    site: 'all'
  });
  const [page, setPage] = useState(0);
  const [availableSites, setAvailableSites] = useState([]);
  const [siteStats, setSiteStats] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenderToDelete, setTenderToDelete] = useState(null);
  const limit = 20;

  useEffect(() => {
    fetchTenders();
    fetchSiteStats();
  }, [filters, page, activeTab]);

  const fetchTenders = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'dismissed') {
        const response = await fetch(`${API_URL}/api/tenders/dismissed?limit=${limit}&skip=${page * limit}`);
        const data = await response.json();
        setDismissedTenders(data.tenders || []);
        setTotalDismissed(data.total || 0);
      } else {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.company && filters.company !== 'all') params.append('company', filters.company);
        if (filters.priority && filters.priority !== 'all') params.append('priority', filters.priority);
        if (filters.site && filters.site !== 'all') params.append('site', filters.site);
        params.append('limit', limit);
        params.append('skip', page * limit);

        const response = await fetch(`${API_URL}/api/tenders?${params}`);
        const data = await response.json();
        setTenders(data.tenders || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching tenders:', error);
      toast.error('Failed to fetch tenders');
    } finally {
      setLoading(false);
    }
  };

  const fetchSiteStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tenders/stats/by-site`);
      const data = await response.json();
      setSiteStats(data.by_site || []);
      setAvailableSites(data.available_sites || []);
    } catch (error) {
      console.error('Error fetching site stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ search: '', company: 'all', priority: 'all', site: 'all' });
    setPage(0);
  };

  const dismissTender = async (tenderId) => {
    try {
      const response = await fetch(`${API_URL}/api/tenders/${tenderId}/dismiss`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        toast.success('Tender dismissed');
        fetchTenders();
        fetchSiteStats();
      } else {
        toast.error('Failed to dismiss tender');
      }
    } catch (error) {
      toast.error('Error dismissing tender');
    }
  };

  const restoreTender = async (tenderId) => {
    try {
      const response = await fetch(`${API_URL}/api/tenders/${tenderId}/restore`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        toast.success('Tender restored');
        fetchTenders();
        fetchSiteStats();
      } else {
        toast.error('Failed to restore tender');
      }
    } catch (error) {
      toast.error('Error restoring tender');
    }
  };

  const deleteTender = async () => {
    if (!tenderToDelete) return;
    
    try {
      const response = await fetch(`${API_URL}/api/tenders/${tenderToDelete}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        toast.success('Tender permanently deleted');
        fetchTenders();
        fetchSiteStats();
      } else {
        toast.error('Failed to delete tender');
      }
    } catch (error) {
      toast.error('Error deleting tender');
    } finally {
      setDeleteDialogOpen(false);
      setTenderToDelete(null);
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toUpperCase()) {
      case 'HIGH': return 'priority-high';
      case 'MEDIUM': return 'priority-medium';
      case 'LOW': return 'priority-low';
      default: return 'priority-low';
    }
  };

  const getCompanyClass = (company) => {
    switch (company?.toUpperCase()) {
      case 'ALEPHTAV': return 'company-alephtav';
      case 'MAU_TECH': return 'company-mautech';
      default: return 'company-irrelevant';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const exportCSV = () => {
    const headers = ['Title', 'Company', 'Priority', 'Site', 'Rationale', 'URL', 'Captured At'];
    const rows = tenders.map(t => [
      t.title?.replace(/[\n,]/g, ' '),
      t.company,
      t.priority,
      t.site_name,
      t.rationale?.replace(/[\n,]/g, ' '),
      t.url,
      t.captured_at
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tenders_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const renderTenderRow = (tender, isDismissed = false) => (
    <tr key={tender._id} data-testid={`tender-row-${tender._id}`}>
      <td>
        <div style={{ fontWeight: 500, marginBottom: 4 }}>
          {tender.title?.substring(0, 100)}...
        </div>
        <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', gap: 6 }}>
          {tender.rationale?.substring(0, 60)}
          {tender.search_term_matched && (
            <Badge variant="outline" className="search-match-badge">
              matched: {tender.search_term_matched}
            </Badge>
          )}
        </div>
      </td>
      <td>
        <span className={`company-badge-inline ${getCompanyClass(tender.company)}`}>
          {tender.company?.replace('_', ' ')}
        </span>
      </td>
      <td>
        <span className={`priority-badge ${getPriorityClass(tender.priority)}`}>
          <span className="priority-dot" />
          {tender.priority}
        </span>
      </td>
      <td>
        <Badge variant="secondary" className="site-badge">
          {tender.site_name || tender.source}
        </Badge>
      </td>
      <td>{formatDate(tender.captured_at)}</td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/tenders/${tender._id}`)}
            data-testid="view-detail-button"
            title="View Details"
          >
            <Eye size={16} />
          </Button>
          {tender.url && (
            <a
              href={tender.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800"
              data-testid="row-open-button"
              title="Open Original"
              style={{ display: 'flex', alignItems: 'center', padding: 8 }}
            >
              <ExternalLink size={16} />
            </a>
          )}
          
          {isDismissed ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => restoreTender(tender._id)}
                title="Restore"
                data-testid="restore-tender-btn"
              >
                <RotateCcw size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTenderToDelete(tender._id);
                  setDeleteDialogOpen(true);
                }}
                title="Delete Permanently"
                className="text-destructive hover:text-destructive"
                data-testid="delete-tender-btn"
              >
                <Trash2 size={16} />
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="tender-actions-menu">
                  <ChevronDown size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => dismissTender(tender._id)} data-testid="dismiss-tender-btn">
                  <Trash2 size={14} className="mr-2" />
                  Dismiss (Not Relevant)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div data-testid="tenders-page">
      {/* Site Stats Summary */}
      {siteStats.length > 0 && (
        <div className="site-stats-bar" data-testid="site-stats-bar">
          {siteStats.map((site, index) => (
            <div 
              key={index} 
              className={`site-stat-item ${filters.site === site._id ? 'active' : ''}`}
              onClick={() => handleFilterChange('site', site._id)}
              data-testid={`site-stat-${site._id}`}
            >
              <Building2 size={14} />
              <span className="site-name">{site._id}</span>
              <Badge variant="secondary">{site.count}</Badge>
            </div>
          ))}
          {filters.site !== 'all' && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleFilterChange('site', 'all')}
              className="clear-site-filter"
            >
              Show All
            </Button>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(0); }} className="tenders-tabs">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <TabsList data-testid="tenders-tabs">
            <TabsTrigger value="active" data-testid="tab-active">
              Active Tenders ({total})
            </TabsTrigger>
            <TabsTrigger value="dismissed" data-testid="tab-dismissed">
              Dismissed ({totalDismissed})
            </TabsTrigger>
          </TabsList>
          
          <Button variant="secondary" onClick={exportCSV} data-testid="export-csv-button">
            <Download size={16} />
            Export CSV
          </Button>
        </div>

        {/* Active Tenders */}
        <TabsContent value="active">
          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="search-input">
              <Input
                placeholder="Search tenders..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                data-testid="global-search-input"
              />
            </div>

            <Select
              value={filters.company}
              onValueChange={(value) => handleFilterChange('company', value)}
            >
              <SelectTrigger className="w-[160px]" data-testid="tenders-filter-company-select">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                <SelectItem value="ALEPHTAV">Alephtav</SelectItem>
                <SelectItem value="MAU_TECH">Mau Tech</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.priority}
              onValueChange={(value) => handleFilterChange('priority', value)}
            >
              <SelectTrigger className="w-[140px]" data-testid="tenders-filter-priority-select">
                <SelectValue placeholder="Any Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Priority</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.site}
              onValueChange={(value) => handleFilterChange('site', value)}
            >
              <SelectTrigger className="w-[180px]" data-testid="tenders-filter-site-select">
                <SelectValue placeholder="All Sites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {availableSites.map((site) => (
                  <SelectItem key={site} value={site}>{site}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="ghost" onClick={clearFilters}>
              Clear
            </Button>
          </div>

          {/* Results count */}
          <div style={{ marginBottom: 16, color: 'hsl(var(--muted-foreground))', fontSize: 14 }}>
            Showing {tenders.length} of {total} tenders
            {filters.site !== 'all' && <span> from <strong>{filters.site}</strong></span>}
          </div>

          {/* Tenders Table */}
          {loading ? (
            <div className="loading-spinner" />
          ) : tenders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Search size={28} />
              </div>
              <div className="empty-state-title">No tenders found</div>
              <div className="empty-state-text">
                {filters.search || filters.company !== 'all' || filters.priority !== 'all' || filters.site !== 'all'
                  ? 'Try adjusting your filters or search terms.'
                  : 'Run a tender scout to start collecting tenders.'}
              </div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table" data-testid="tenders-table">
                <thead>
                  <tr>
                    <th style={{ width: '35%' }}>Title</th>
                    <th>Company</th>
                    <th>Priority</th>
                    <th>Source</th>
                    <th>Captured</th>
                    <th style={{ width: 130 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenders.map((tender) => renderTenderRow(tender, false))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Dismissed Tenders */}
        <TabsContent value="dismissed">
          <div style={{ marginBottom: 16, color: 'hsl(var(--muted-foreground))', fontSize: 14 }}>
            Showing {dismissedTenders.length} of {totalDismissed} dismissed tenders
          </div>

          {loading ? (
            <div className="loading-spinner" />
          ) : dismissedTenders.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Trash2 size={28} />
              </div>
              <div className="empty-state-title">No dismissed tenders</div>
              <div className="empty-state-text">
                Tenders you dismiss will appear here. You can restore or permanently delete them.
              </div>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="data-table" data-testid="dismissed-tenders-table">
                <thead>
                  <tr>
                    <th style={{ width: '35%' }}>Title</th>
                    <th>Company</th>
                    <th>Priority</th>
                    <th>Source</th>
                    <th>Dismissed</th>
                    <th style={{ width: 130 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dismissedTenders.map((tender) => renderTenderRow(tender, true))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Pagination */}
      {((activeTab === 'active' && total > limit) || (activeTab === 'dismissed' && totalDismissed > limit)) && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <Button
            variant="secondary"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span style={{ display: 'flex', alignItems: 'center', padding: '0 16px', color: 'hsl(var(--muted-foreground))' }}>
            Page {page + 1} of {Math.ceil((activeTab === 'active' ? total : totalDismissed) / limit)}
          </span>
          <Button
            variant="secondary"
            disabled={(page + 1) * limit >= (activeTab === 'active' ? total : totalDismissed)}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Tender?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The tender will be permanently removed from the database.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteTender} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default Tenders;
